import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2, Globe, Lock, CheckCircle2, Clock, Send, Radio, Link2 } from "lucide-react";
import { FormFieldInput, FIELD_META, isFieldEmpty, visibleFields } from "@/components/FormFieldKit";
import { LiveFormEditor } from "@/components/LiveFormEditor";

const KIND_LABEL = { brief: "Brief", document_request: "Document Request", general: "General" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export const copyFormLink = async (slug, fid) => {
  const url = `${window.location.origin}/${slug}/forms/${fid}`;
  try { await navigator.clipboard.writeText(url); toast.success("Form link copied"); } catch { toast.message(url); }
};

export default function ClientForms() {
  const { slug, formId } = useParams();
  const navigate = useNavigate();
  const [forms, setForms] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [liveId, setLiveId] = useState(null);
  const openForm = (id) => navigate(`/${slug}/forms/${id}`);
  const closeForm = () => { setOpenId(null); setLiveId(null); navigate(`/${slug}/forms`); };
  useEffect(() => {
    if (!forms) return;
    if (!formId) { setOpenId(null); setLiveId(null); return; }
    const f = forms.find((x) => x.id === formId);
    if (!f) { toast.error("This form isn't available in your workspace"); navigate(`/${slug}/forms`, { replace: true }); return; }
    if (f.mode === "live") { setOpenId(null); setLiveId(f.id); } else { setLiveId(null); setOpenId(f.id); }
  }, [formId, forms]); // eslint-disable-line

  const load = async () => {
    try { const { data } = await api.get("/forms"); setForms(data.forms || []); }
    catch (e) { setForms([]); }
  };
  useEffect(() => { load(); }, []);

  return (
    <main className="px-6 md:px-10 py-10 max-w-4xl mx-auto" data-testid="client-forms-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] flex items-center gap-2.5"><FileText className="h-7 w-7 text-[#723FFF]" /> Forms</h1>
        <p className="mt-2 text-sm text-[#9FA1A4]">Forms shared with you. Fill them in and track what you've submitted.</p>
      </div>

      {forms === null ? <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto" /></div>
        : forms.length === 0 ? <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-20 text-center"><FileText className="h-10 w-10 text-[#d3d4d8] mx-auto mb-3" /><p className="text-sm text-[#9FA1A4]">No forms have been shared with you yet.</p></div>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {forms.map((f) => (
              <div key={f.id} role="button" tabIndex={0} data-testid="client-form-card" onClick={() => openForm(f.id)} onKeyDown={(e) => e.key === "Enter" && openForm(f.id)} className="relative text-left rounded-2xl border border-[#EEEFEF] bg-white p-5 hover:border-[#d9ccff] hover:shadow-sm transition-all cursor-pointer">
                <button data-testid={`client-form-copy-link-${f.id}`} title="Copy direct link" onClick={(e) => { e.stopPropagation(); copyFormLink(slug, f.id); }} className="absolute bottom-3 right-3 h-7 w-7 rounded-full flex items-center justify-center text-[#9FA1A4] hover:text-[#723FFF] hover:bg-[#F6F6FD]"><Link2 className="h-3.5 w-3.5" /></button>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6] bg-[#EDE7FF] rounded-full px-2 py-0.5">{KIND_LABEL[f.kind] || f.kind}</span>
                  {f.mode === "live" ? <span data-testid="client-form-live-badge" className={`font-mono-data text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1 ${f.live?.status === "finalised" ? "bg-[#1f9d55]/10 text-[#1f9d55]" : "bg-[#EDE7FF] text-[#5b2fd6]"}`}><Radio className="h-3 w-3" />{f.live?.status === "finalised" ? "Finalised" : "Live"}</span>
                    : f.visibility === "public" ? <Globe className="h-4 w-4 text-[#1f9d55]" /> : <Lock className="h-4 w-4 text-[#c4c5cc]" />}
                </div>
                <p className="font-display font-semibold text-[#06060C]">{f.title}</p>
                {f.description && <p className="text-xs text-[#9FA1A4] mt-1 line-clamp-2">{f.description}</p>}
                {f.mode === "live" && <p className="mt-3 pr-8 text-[11px] text-[#595D62]" data-testid="client-form-live-status">{f.live?.editing ? `${f.live.editing} is editing now` : f.live?.last_saved_at ? `Last saved ${fmtDate(f.live.last_saved_at)} by ${f.live.last_saved_by}` : "Shared form: anyone on your team can work on it"}</p>}
                {f.mode !== "live" && f.my_submission_count > 0 && <p className="mt-3 text-[11px] text-[#1f9d55] inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Submitted {f.my_submission_count}×</p>}
              </div>
            ))}
          </div>
        )}

      {liveId && <LiveFormEditor key={liveId} base={`/forms/${liveId}/live`} uploadUrl="/forms/upload" onClose={closeForm} onChanged={load} />}
      {openId && <FillFormDialog key={openId} fid={openId} onClose={closeForm} onSubmitted={load} />}
    </main>
  );
}

function FillFormDialog({ fid, onClose, onSubmitted }) {
  const [f, setF] = useState(null);
  const [values, setValues] = useState({});
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState(0);

  const loadSubs = () => api.get(`/forms/${fid}/my-submissions`).then(({ data }) => setSubs(data.submissions || [])).catch(() => {});
  useEffect(() => {
    api.get(`/forms/${fid}`).then(({ data }) => setF(data)).catch(() => { toast.error("Form unavailable"); onClose(); });
    api.post(`/forms/${fid}/opened`).catch(() => {});
    loadSubs(); /* eslint-disable-next-line */
  }, [fid]);

  const uploader = async (file) => {
    setUploads((n) => n + 1);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/forms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); return data; }
    finally { setUploads((n) => Math.max(0, n - 1)); }
  };
  const setVal = (id, v) => setValues((cur) => ({ ...cur, [id]: v }));

  const submit = async () => {
    for (const fld of visibleFields(f.fields || [], values)) {
      if (fld.required && FIELD_META[fld.type]?.input !== false) {
        if (isFieldEmpty(fld, values[fld.id])) { toast.error(`Please complete: ${fld.label}`); return; }
      }
    }
    setBusy(true);
    const data = {};
    visibleFields(f.fields || [], values).forEach((fld) => { if (values[fld.id] !== undefined) data[fld.id] = values[fld.id]; });
    try { await api.post(`/forms/${fid}/submit`, { data }); toast.success("Submitted — thank you!"); setValues({}); await loadSubs(); onSubmitted && onSubmitted(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-lg max-h-[88vh] overflow-y-auto" data-testid="client-form-fill">
        {!f ? <div className="py-16 text-center"><DialogTitle className="sr-only">Form</DialogTitle><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div> : (
          <>
            <DialogHeader><DialogTitle className="font-display">{f.title}</DialogTitle>{f.description && <DialogDescription className="text-[#9FA1A4]">{f.description}</DialogDescription>}</DialogHeader>
            {subs.length > 0 && (
              <div className="rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] p-3">
                <p className="text-[11px] font-mono-data uppercase tracking-wider text-[#9FA1A4] mb-1.5 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Your submissions</p>
                <div className="space-y-1">{subs.map((s) => <p key={s.id} data-testid="client-form-mysub" className="text-xs text-[#595D62] flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[#1f9d55]" /> {fmtDate(s.created_at)}</p>)}</div>
              </div>
            )}
            <div className="space-y-4 py-1">
              {visibleFields(f.fields || [], values).map((fld) => (
                <FormFieldInput key={fld.id} field={fld} value={values[fld.id]} onChange={(v) => setVal(fld.id, v)} uploader={uploader} />
              ))}
              {(f.fields || []).length === 0 && <p className="text-sm text-[#9FA1A4]">This form has no fields yet.</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-[#EEEFEF] bg-transparent rounded-full">Close</Button>
              <Button data-testid="client-form-submit" onClick={submit} disabled={busy || uploads > 0 || !(f.fields || []).length} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : uploads > 0 ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading files…</> : <><Send className="h-4 w-4 mr-1.5" /> Submit</>}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
